import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import { DemoSelectControl } from '@/components/Demo';
import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';

import GridDemo from './GridDemo';
import BaseItem from './Item';
import {
  GridStructure,
  BaseCodeWrapper,
  LabeledColumn,
} from './Grid.shared';

function JustifyContent({ mode }) {
  return (
    <GridDemo
      initialValues={{
        justifyContent: 'start',
        justifyItem: 'stretch',
      }}
      defaultPerspectiveEnabled={false}
      controls={[
        <DemoSelectControl
          key="justifyContent"
          id="justifyContent"
          label="justify-content"
          options={[
            { label: 'start (default)', value: 'start' },
            'center',
            'end',
            'space-between',
            'space-around',
            'space-evenly',
          ]}
        />,
        <DemoSelectControl
          key="justifyItem"
          id="justifyItem"
          label={mode === 'items' ? 'justify-items' : 'justify-self'}
          options={[
            { label: 'stretch (default)', value: 'stretch' },
            'start',
            'center',
            'end',
          ]}
          wrapperStyle={{
            color:
              mode === 'self' ? 'hsl(50deg 100% 65%)' : undefined,
          }}
          style={
            mode === 'self'
              ? {
                  color: 'black',
                  background: 'hsl(50deg 100% 50%)',
                }
              : undefined
          }
        />,
      ]}
    >
      {({ justifyContent, justifyItem, isPerspectiveEnabled }) => {
        const gridTemplateColumns = '100px 100px';
        let cssCode =
          mode === 'items'
            ? `\
.parent {
  display: grid;
  grid-template-columns: 90px 90px;
  justify-content: ${justifyContent};
  justify-items: ${justifyItem};
}`
            : `\
.parent {
  display: grid;
  grid-template-columns: 90px 90px;
  justify-content: ${justifyContent};
}

.one {
  justify-self: ${justifyItem};
}`;

        return (
          <Row>
            <CodeWrapper>
              <ClientOnlySnippet
                omitCopyButton
                lockedToColorMode="dark"
                code={cssCode}
                lang="css"
              />
            </CodeWrapper>
            <Wrapper layout layoutRoot>
              <BackLayer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <GridStructure
                  style={{
                    gridTemplateColumns,
                    justifyContent,
                    gap: 2,
                  }}
                >
                  <LabeledColumn layout={!isPerspectiveEnabled}>
                    C1
                  </LabeledColumn>
                  <LabeledColumn layout={!isPerspectiveEnabled}>
                    C2
                  </LabeledColumn>
                  <GridRow />
                </GridStructure>
              </BackLayer>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translateX(-10%) skewY(18deg) scaleX(0.85)'
                    : 'translateX(0%) skewY(0deg) scaleX(1)',
                }}
              >
                <ChildWrapper
                  style={{
                    gridTemplateColumns,
                    justifyContent,
                    justifyItems:
                      mode === 'items' ? justifyItem : undefined,
                  }}
                >
                  <Item
                    layout={!isPerspectiveEnabled}
                    primary={mode === 'self'}
                    style={{
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                      justifySelf:
                        mode === 'self' ? justifyItem : undefined,
                    }}
                  >
                    One
                  </Item>
                  <Item
                    layout={!isPerspectiveEnabled}
                    style={{
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                    }}
                  >
                    Two
                  </Item>
                  <Item
                    layout={!isPerspectiveEnabled}
                    style={{
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                    }}
                  >
                    Three
                  </Item>
                  <Item
                    layout={!isPerspectiveEnabled}
                    style={{
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                    }}
                  >
                    Four
                  </Item>
                </ChildWrapper>
              </Layer>
            </Wrapper>
          </Row>
        );
      }}
    </GridDemo>
  );
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;
  margin: 64px 0px 16px;
  isolation: isolate;

  @media (max-width: 47.5rem) {
    flex-direction: column-reverse;
    gap: 32px;
    align-items: center;
  }
`;

const Wrapper = styled(motion.div)`
  position: relative;
  width: 260px;
  max-width: calc(100vw - 32px);
  min-height: 190px;
  max-height: 220px;

  @media (max-width: 47.5rem) {
    width: 386px;
  }
`;

const Layer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: transform 500ms;
  will-change: transform;
`;

const BackLayer = styled(Layer)`
  background: repeating-linear-gradient(
    45deg,
    hsl(210deg 40% 80% / 0.06) 0px,
    hsl(210deg 40% 80% / 0.06) 10px,
    hsl(210deg 40% 80% / 0) 10px,
    hsl(210deg 40% 80% / 0) 20px
  );
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  gap: 6px 2px;
  padding: 2px;
`;

const Item = styled(BaseItem)`
  margin: 0px 3px;
  font-size: 0.875rem;
`;

const GridRow = styled.div`
  position: absolute;
  top: calc(50% - 1px);
  left: 0;
  right: 0;
  width: 100%;
  border-top: 2px dashed var(--color-gray-500);
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  position: relative;
  z-index: 2;
  width: 388px;
  flex-grow: 1;

  @media (max-width: 27.5rem) {
    width: calc(100% + 32px);
    max-width: calc(100% + 32px);
    margin-inline: -16px;
    padding-inline: 3px;
    border-radius: 0;
  }
`;

export default JustifyContent;
